import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { ClipboardCheck, Brain, FileText, ChevronDown, ChevronUp, Sparkles, Loader2 } from 'lucide-react';
import QualificationResultCard from '../QualificationResultCard';
import KeepRejectActions from '../KeepRejectActions';

const CATEGORY_LABELS = {
  'assistant_virtuel': 'Assistant Virtuel',
  'community_manager': 'Community Manager',
  'gestion_ads': 'Gestion Ads / Traffic Manager',
  'seo': 'SEO / Référencement',
  'developpeur': 'Développeur / Tech',
  'graphiste': 'Graphiste / Designer',
  'redacteur': 'Rédacteur / Copywriter',
  'comptabilite': 'Comptabilité / Finance',
  'support_client': 'Support Client',
  'commercial': 'Commercial / Vente',
  'autre': 'Autre'
};

const QualificationTab = ({ candidate, candidateId, qualification, updateStatus, determineFormType }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [rawAnswersOpen, setRawAnswersOpen] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  const handleReanalyze = async () => {
    if (!qualification?._id && !qualification?.id) return;
    const qid = qualification._id || qualification.id;
    setReanalyzing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/qualifications/${qid}/reanalyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(`Re-analyse OK · Score ${data.score_total}/100 · ${data.per_question_count} questions notées`);
      // Reload to show fresh data
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(`Re-analyse échouée : ${e.message}`);
    } finally {
      setReanalyzing(false);
    }
  };

  if (qualification) {
    const rawAnswers = Array.isArray(qualification.answers) ? qualification.answers : [];
    const answeredAnswers = rawAnswers.filter(a => (a?.answer ?? '').toString().trim().length > 0);
    const groupedByCategory = answeredAnswers.reduce((acc, a) => {
      const cat = a.category || 'Autres';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(a);
      return acc;
    }, {});
    const categoryOrder = Object.keys(groupedByCategory);

    // Lookup map: question_id -> per_question feedback (V6)
    const perQuestion = Array.isArray(qualification.per_question) ? qualification.per_question : [];
    const perQuestionMap = perQuestion.reduce((acc, pq) => {
      if (pq?.question_id) acc[pq.question_id] = pq;
      return acc;
    }, {});

    const verdictStyle = {
      excellent: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'Excellent' },
      bon: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'Bon' },
      correct: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', label: 'Correct' },
      faible: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Faible' },
    };

    return (
      <div className="space-y-4 md:space-y-6">
        <QualificationResultCard qualification={qualification} />

        {rawAnswers.length > 0 && (
          <Card className="border-tp-border" data-testid="raw-answers-card">
            <button
              type="button"
              onClick={() => setRawAnswersOpen(o => !o)}
              className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-tp-bg/60 transition-colors rounded-t-lg"
              data-testid="raw-answers-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-tp-primary/10 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-tp-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-tp-text">Réponses du candidat</h3>
                  <p className="text-xs text-tp-text-light">
                    {answeredAnswers.length} question{answeredAnswers.length > 1 ? 's' : ''} répondue{answeredAnswers.length > 1 ? 's' : ''}
                    {rawAnswers.length !== answeredAnswers.length && ` / ${rawAnswers.length} au total`}
                  </p>
                </div>
              </div>
              {rawAnswersOpen ? <ChevronUp className="h-5 w-5 text-tp-text-light" /> : <ChevronDown className="h-5 w-5 text-tp-text-light" />}
            </button>
            {rawAnswersOpen && (
              <div className="border-t border-tp-border p-4 md:p-5 space-y-5" data-testid="raw-answers-content">
                {categoryOrder.map((cat) => (
                  <div key={cat} data-testid={`raw-answers-category-${cat}`}>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-tp-text-light mb-3 pb-1 border-b border-tp-border/60">
                      {cat}
                    </h4>
                    <div className="space-y-4">
                      {groupedByCategory[cat].map((a, idx) => {
                        const pq = a.question_id ? perQuestionMap[a.question_id] : null;
                        const vStyle = pq && verdictStyle[pq.verdict] ? verdictStyle[pq.verdict] : null;
                        return (
                          <div key={`${cat}-${a.question_id || idx}`} className="rounded-md bg-tp-bg/40 border border-tp-border/50 p-3" data-testid={`raw-answer-${a.question_id || idx}`}>
                            <p className="text-xs font-medium text-tp-text-light mb-1">Q. {a.question || a.question_id}</p>
                            <p className="text-sm text-tp-text whitespace-pre-wrap">{a.answer || <span className="italic text-gray-400">— Non répondu —</span>}</p>
                            {pq && (
                              <div className={`mt-3 rounded-md border ${vStyle?.border || 'border-gray-200'} ${vStyle?.bg || 'bg-gray-50'} p-2.5`} data-testid={`per-question-feedback-${a.question_id}`}>
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className={`text-xs font-semibold uppercase tracking-wide ${vStyle?.text || 'text-gray-600'}`}>
                                    Note IA · {vStyle?.label || pq.verdict}
                                  </span>
                                  <span className={`text-sm font-bold ${vStyle?.text || 'text-gray-700'}`}>
                                    {pq.score}/100
                                  </span>
                                </div>
                                {pq.comment && (
                                  <p className={`text-xs leading-snug ${vStyle?.text || 'text-gray-600'}`}>
                                    {pq.comment}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <Card className="p-4 md:p-6 border-tp-border">
          <h3 className="text-base md:text-lg font-display font-semibold text-tp-text mb-3 md:mb-4">Décision</h3>
          <KeepRejectActions
            candidateId={candidateId}
            currentStatus={candidate?.status}
            size="lg"
            onChanged={() => setTimeout(() => window.location.reload(), 600)}
            testIdPrefix="qualif-"
          />
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-tp-border/50">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReanalyze}
              disabled={reanalyzing}
              className="border-tp-primary/40 text-tp-primary hover:bg-tp-primary/5"
              data-testid="reanalyze-ai-btn"
              title="Relance GPT-4o sur les réponses stockées. Le candidat n'est pas contacté."
            >
              {reanalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {reanalyzing ? 'Re-analyse...' : 'Re-analyser IA'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/qualify/${candidateId}/${qualification.profile_type || 'assistant_virtuel'}`)} className="border-tp-border" data-testid="requalify-btn">
              <ClipboardCheck className="h-4 w-4 mr-2" />Requalifier (manuel)
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const recommendedForm = determineFormType(candidate);
  return (
    <Card className="p-6 md:p-8 border-tp-border text-center">
      <Brain className="h-10 w-10 md:h-12 md:w-12 text-tp-text-light mx-auto mb-4" />
      <h3 className="text-lg font-display font-semibold text-tp-text mb-2">Aucune qualification disponible</h3>
      <p className="text-tp-text-light mb-6">Ce candidat n'a pas encore été évalué.</p>
      
      <div className="bg-tp-bg rounded-lg p-4 mb-6">
        <p className="text-sm text-tp-text-light mb-2">Formulaire recommandé :</p>
        <Badge className="bg-tp-primary text-white text-sm px-3 py-1">{recommendedForm.label}</Badge>
        {candidate?.primary_category && (
          <p className="text-xs text-tp-text-light mt-2">Catégorie : {CATEGORY_LABELS[candidate.primary_category] || candidate.primary_category}</p>
        )}
      </div>
      
      <Button onClick={() => navigate(`/admin/qualify/${candidateId}/${recommendedForm.type}`)} className="bg-tp-primary hover:bg-tp-primary-light text-white mb-4" size="lg" data-testid="qualify-recommended-btn">
        <ClipboardCheck className="h-5 w-5 mr-2" />Démarrer l'évaluation ({recommendedForm.label})
      </Button>
      
      <div className="border-t border-tp-border pt-4 mt-4">
        <p className="text-xs text-tp-text-light mb-3">Ou choisir un autre formulaire :</p>
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            { type: 'assistant_virtuel', label: 'Assistant Virtuel' },
            { type: 'talent_technique', label: 'Tech' },
            { type: 'marketing_ads', label: 'Marketing' },
            { type: 'seo', label: 'SEO' },
            { type: 'community_manager', label: 'CM' }
          ].filter(f => f.type !== recommendedForm.type).map(form => (
            <Button key={form.type} variant="outline" size="sm" onClick={() => navigate(`/admin/qualify/${candidateId}/${form.type}`)} className="border-tp-border text-xs" data-testid={`qualify-${form.type}-btn`}>
              {form.label}
            </Button>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default QualificationTab;
